import React, { useState, useEffect } from 'react';

// ── Detection helpers ──────────────────────────────────────────────────────
const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

// True only when the app is already running from the home screen
const isStandalone = () =>
  window.navigator.standalone === true ||
  window.matchMedia('(display-mode: standalone)').matches;

// On iOS, only Mobile Safari can install PWAs.
// Chrome iOS sets "CriOS", Firefox iOS sets "FxiOS".
const isIOSSafari = () =>
  isIOS() &&
  /Safari/.test(navigator.userAgent) &&
  !/CriOS|FxiOS|OPiOS|mercury/.test(navigator.userAgent);

const isIOSOtherBrowser = () =>
  isIOS() && !isIOSSafari() && !isStandalone();

const DISMISS_KEY   = 'ios_install_banner_snoozed_until';
const SNOOZE_DAYS   = 7;

// ── Component ──────────────────────────────────────────────────────────────
const IOSInstallBanner = () => {
  const [visible, setVisible] = useState(false);
  const [otherBrowser, setOtherBrowser] = useState(false);

  useEffect(() => {
    // Don't show on non-iOS or when already installed as PWA
    if (!isIOS() || isStandalone()) return;

    // Check snooze
    const snoozedUntil = localStorage.getItem(DISMISS_KEY);
    if (snoozedUntil && Date.now() < Number(snoozedUntil)) return;

    setOtherBrowser(isIOSOtherBrowser());
    setVisible(true);
  }, []);

  const dismiss = () => {
    const until = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Install app on iPhone"
      className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up"
    >
      {/* Backdrop tap-to-close */}
      <div
        className="fixed inset-0 bg-black/30"
        onClick={dismiss}
        aria-hidden="true"
      />

      {/* Card */}
      <div className="relative bg-white rounded-t-2xl shadow-2xl px-5 pt-5 pb-8 mx-0">
        {/* Drag handle */}
        <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4" />

        {/* Close */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1"
          aria-label="Dismiss"
        >
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {/* Bell icon + heading */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900 leading-tight">
              Enable Push Notifications
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Get notified about your rides in real-time
            </p>
          </div>
        </div>

        {otherBrowser ? (
          /* ── Non-Safari iOS browser ──────────────────────────────────── */
          <div className="bg-amber-50 rounded-xl p-4 mt-1">
            <p className="text-sm text-amber-800 font-medium mb-1">
              Open in Safari first
            </p>
            <p className="text-sm text-amber-700 leading-relaxed">
              iOS only allows push notifications for apps installed via <strong>Safari</strong>.
              Copy the link below and open it in Safari, then follow the install steps.
            </p>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(window.location.href);
              }}
              className="mt-3 flex items-center gap-2 text-sm font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 active:bg-amber-300 px-3 py-2 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy page link
            </button>
          </div>
        ) : (
          /* ── Safari iOS — show install steps ────────────────────────── */
          <>
            <p className="text-sm text-gray-600 mb-4">
              Install this app on your iPhone for the best experience and real-time ride updates.
            </p>

            <ol className="space-y-3">
              {/* Step 1 */}
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                  1
                </span>
                <div className="text-sm text-gray-700 leading-snug">
                  Tap the{' '}
                  <strong>Share</strong> button at the bottom of Safari
                  {/* iOS Share icon inline */}
                  <span className="inline-flex items-center justify-center ml-1 mr-0.5 w-5 h-5 bg-gray-100 rounded align-text-bottom">
                    <svg className="w-3.5 h-3.5 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M16 8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  </span>
                </div>
              </li>

              {/* Step 2 */}
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                  2
                </span>
                <div className="text-sm text-gray-700 leading-snug">
                  Scroll down and tap{' '}
                  <strong className="inline-flex items-center gap-1">
                    Add to Home Screen
                    <svg className="w-3.5 h-3.5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  </strong>
                </div>
              </li>

              {/* Step 3 */}
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                  3
                </span>
                <div className="text-sm text-gray-700 leading-snug">
                  Tap <strong>Add</strong> in the top-right corner, then open the app and log in
                </div>
              </li>
            </ol>

            {/* Visual hint arrow pointing to the share button area */}
            <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              The Share button is in the Safari toolbar below
            </div>
          </>
        )}

        {/* Dismiss link */}
        <button
          onClick={dismiss}
          className="mt-5 w-full text-center text-sm text-gray-400 hover:text-gray-600 py-1"
        >
          Remind me later
        </button>
      </div>
    </div>
  );
};

export default IOSInstallBanner;
